import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Building2, Users, FileText, ArrowLeftRight, BarChart3 } from "lucide-react";

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ assigned_funds: 0, total_investors: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/dashboard/stats")
      .then(({ data }) => setStats(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const cards = [
    { title: "Assigned Funds", value: stats.assigned_funds, icon: Building2, color: "text-blue-500 dark:text-blue-400", bg: "bg-blue-500/10 border-blue-500/20", link: "/admin/fund" },
    { title: "Total Investors", value: stats.total_investors, icon: Users, color: "text-emerald-500 dark:text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20", link: "/admin/investors" },
  ];

  const actions = [
    { label: "Fund Profile", icon: Building2, path: "/admin/fund", color: "text-blue-500 dark:text-blue-400" },
    { label: "Manage Investors", icon: Users, path: "/admin/investors", color: "text-emerald-500 dark:text-emerald-400" },
    { label: "Documents", icon: FileText, path: "/admin/documents", color: "text-amber-500 dark:text-amber-400" },
    { label: "Transactions", icon: ArrowLeftRight, path: "/admin/transactions", color: "text-violet-500 dark:text-violet-400" },
    { label: "Reporting", icon: BarChart3, path: "/admin/reporting", color: "text-pink-500 dark:text-pink-400" },
  ];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="admin-dashboard">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Welcome, {user?.name}</h1>
        <p className="text-muted-foreground text-sm mt-1">Manage your assigned funds and investors</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {cards.map((card) => (
          <Card key={card.title} className={`stat-card ${card.bg}`} data-testid={`stat-${card.title.toLowerCase().replace(/\s+/g, "-")}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{card.title}</span>
                <card.icon className={`w-5 h-5 ${card.color}`} strokeWidth={1.5} />
              </div>
              <p className="font-heading text-3xl font-bold">
                {loading ? <span className="inline-block w-12 h-8 bg-secondary rounded animate-pulse" /> : card.value}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card data-testid="admin-quick-actions">
        <CardHeader className="p-6 pb-3">
          <CardTitle className="font-heading text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="p-6 pt-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {actions.map((action) => (
            <Button key={action.path} asChild variant="secondary" className="justify-start border border-border h-11">
              <Link to={action.path}>
                <action.icon className={`w-4 h-4 mr-3 ${action.color}`} /> {action.label}
              </Link>
            </Button>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
